import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '../../services/api';
import SidebarPanel from '../../components/admin/SidebarPanel';
import AgGridTable from '../../components/AgGridTable';
import AlertMessage from '../../components/shared/alertMessage';

const HealthSystemsPage = () => {
  const navigate = useNavigate();
  const { healthSystemId } = useParams();
  
  const [healthSystems, setHealthSystems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [activeTab, setActiveTab] = useState('cases');
  const [relatedData, setRelatedData] = useState({
    cases: [],
    users: [],
    providers: [],
    patients: [],
    departments: [],
    clinics: []
  });
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [alertState, setAlertState] = useState({ show: false, type: 'info', title: '', message: '' });

  // Custom Cell Renderer for Actions column
  const ActionsCellRenderer = (props) => {
    const { data } = props;
    if (!data) return null;

    return (
      <div className="btn-group btn-group-sm" role="group">
        <button
          className="btn btn-outline-primary"
          onClick={() => handleSelectSystem(data)}
          title="View Details"
        >
          <i className="far fa-eye me-1"></i>
          View
        </button>
        <Link
          to={`/admin/health-systems/edit/${data.health_system_id}`}
          className="btn btn-outline-info disabled"
          title="Edit"
        >
          <i className="fa fa-pencil me-1"></i>
          Edit
        </Link>
        <button
          className="btn btn-outline-danger disabled"
          onClick={() => {
            if (window.confirm('Are you sure you want to delete this health system?')) {
              console.log('Delete health system:', data.id);
            }
          }}
          title="Delete"
        >
          <i className="far fa-trash-can me-1"></i>
          Delete
        </button>
      </div>
    );
  };

  // ag-Grid Column Definitions
  const [columnDefs] = useState([
    { 
      headerName: 'ID', 
      field: 'id', 
      width: 80 
    },
    {
      headerName: 'Name',
      field: 'name',
      width: 200,
      cellRenderer: (params) => {
        if (params.data) {
          return (
            <a
              href="#"
              className="text-body fw-bold"
              onClick={(e) => {
                e.preventDefault();
                handleSelectSystem(params.data);
              }}
            >
              {params.data.name}
            </a>
          );
        }
        return null;
      }
    },
    {
      headerName: 'Location',
      field: 'city',
      width: 150,
      valueGetter: (p) => {
        const data = p.data;
        if (!data) return 'N/A';
        if (data.city && data.state) return `${data.city}, ${data.state}`;
        if (data.city) return data.city;
        if (data.state) return data.state;
        return 'N/A';
      }
    },
    {
      headerName: 'Contact',
      field: 'email',
      width: 200,
      valueGetter: (p) => p.data?.email || p.data?.phone || 'N/A'
    },
    {
      headerName: 'Status',
      field: 'is_active',
      width: 100,
      cellRenderer: (params) => {
        if (params.data) {
          // Default to true (Active) if is_active is undefined/null
          const isActive = params.data.is_active !== false;
          return (
            <span className={`badge ${isActive ? 'bg-success' : 'bg-danger'}`}>
              {isActive ? 'Active' : 'Inactive'}
            </span>
          );
        }
        return null;
      },
      valueGetter: (params) => {
        // For filtering purposes, return a consistent value
        return params.data?.is_active !== false ? 'Active' : 'Inactive';
      }
    },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: ActionsCellRenderer,
      minWidth: 250,
      sortable: false,
      filter: false
    }
  ]);
  const [showDetailsSidebar, setShowDetailsSidebar] = useState(false);

  // Fetch all health systems on initial load
  useEffect(() => {
    const fetchHealthSystems = async () => {
      try {
        setIsLoading(true);
        const response = await adminApi.getHealthSystems();
        if (response.data.success) {
          setHealthSystems(response.data.health_systems);
        } else {
          setError('Failed to load health systems');
        }
      } catch (err) {
        console.error('Error fetching health systems:', err);
        if (err.response && err.response.status === 401) {
          setError('API token expired. Please log in again.');
          // Redirect to admin login page
          navigate('/admin/login');
        } else if (err.response && err.response.status === 403) {
          setError('Permission denied. This feature requires proper authentication and authorization.');
        } else {
          setError(`Error: ${err.message}`);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchHealthSystems();
  }, [navigate]);

  // Handle URL parameter - automatically open sidebar if healthSystemId is in URL
  useEffect(() => {
    if (healthSystemId && healthSystems.length > 0 && !selectedSystem) {
      const system = healthSystems.find(hs => hs.id === healthSystemId || hs.health_system_id === healthSystemId);
      if (system) {
        handleSelectSystem(system);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthSystemId, healthSystems, selectedSystem]);


  // Helper function to format error messages for users
  const formatErrorMessage = (errorMsg) => {
    if (!errorMsg) return 'Failed to load';

    // Remove technical array/list formatting
    let cleaned = errorMsg.replace(/^\[|\]$/g, '').replace(/^['"]|['"]$/g, '');

    // Simplify UUID validation errors
    if (cleaned.includes('Invalid UUID format') || cleaned.includes('badly formed hexadecimal UUID string')) {
      return 'Invalid identifier format';
    }

    // Simplify other common errors
    if (cleaned.includes('does not exist') || cleaned.includes('not found')) {
      return 'Not found';
    }

    // If still too long, truncate
    if (cleaned.length > 80) {
      cleaned = cleaned.substring(0, 77) + '...';
    }

    return cleaned;
  };

  // Function to fetch related data for a selected health system
  const fetchRelatedData = async (system) => {
    try {
      setLoadingRelated(true);
      setError(null);

      const systemId = system.health_system_id;
      const systemUuid = system.id;

      // Make parallel API calls to fetch all related data
      // Note: Users endpoint requires UUID, all others use health_system_id
      // Using allSettled to handle individual failures gracefully
      const results = await Promise.allSettled([
        adminApi.getHealthSystemCases(systemId),
        adminApi.getHealthSystemUsers(systemUuid),
        adminApi.getHealthSystemProviders(systemId),
        adminApi.getHealthSystemPatients(systemId),
        adminApi.get(`/admin/health-system/${systemId}/departments/`),
        adminApi.get(`/admin/health-system/${systemId}/clinics/`)
      ]);

      const [casesResult, usersResult, providersResult, patientsResult, departmentsResult, clinicsResult] = results;

      // Collect all errors
      const errors = [];

      // Process cases data
      let cases = [];
      if (casesResult.status === 'fulfilled' && casesResult.value.data.success) {
        cases = casesResult.value.data.cases || [];
      } else if (casesResult.status === 'rejected') {
        const errMsg = casesResult.reason?.response?.data?.message || casesResult.reason?.message || `Request failed (${casesResult.reason?.response?.status || 'Network Error'})`;
        errors.push(`Cases: ${formatErrorMessage(errMsg)}`);
      } else {
        errors.push(`Cases: ${formatErrorMessage(casesResult.value.data.message)}`);
      }

      // Process users data
      let users = [];
      if (usersResult.status === 'fulfilled' && usersResult.value.data.success) {
        users = usersResult.value.data.users || [];
      } else if (usersResult.status === 'rejected') {
        const errMsg = usersResult.reason?.response?.data?.message || usersResult.reason?.message || `Request failed (${usersResult.reason?.response?.status || 'Network Error'})`;
        errors.push(`Users: ${formatErrorMessage(errMsg)}`);
      } else {
        errors.push(`Users: ${formatErrorMessage(usersResult.value.data.message)}`);
      }

      // Process providers data
      let providers = [];
      if (providersResult.status === 'fulfilled' && providersResult.value.data.success) {
        providers = providersResult.value.data.providers || [];
      } else if (providersResult.status === 'rejected') {
        const errMsg = providersResult.reason?.response?.data?.message || providersResult.reason?.message || `Request failed (${providersResult.reason?.response?.status || 'Network Error'})`;
        errors.push(`Providers: ${formatErrorMessage(errMsg)}`);
      } else {
        errors.push(`Providers: ${formatErrorMessage(providersResult.value.data.message)}`);
      }

      // Process patients data
      let patients = [];
      if (patientsResult.status === 'fulfilled' && patientsResult.value.data.success) {
        patients = patientsResult.value.data.patients || [];
      } else if (patientsResult.status === 'rejected') {
        const errMsg = patientsResult.reason?.response?.data?.message || patientsResult.reason?.message || `Request failed (${patientsResult.reason?.response?.status || 'Network Error'})`;
        errors.push(`Patients: ${formatErrorMessage(errMsg)}`);
      } else {
        errors.push(`Patients: ${formatErrorMessage(patientsResult.value.data.message)}`);
      }

      // Process departments data
      let departments = [];
      if (departmentsResult.status === 'fulfilled' && departmentsResult.value.data.success) {
        departments = departmentsResult.value.data.departments || [];
      } else if (departmentsResult.status === 'rejected') {
        const errMsg = departmentsResult.reason?.response?.data?.message || departmentsResult.reason?.message || `Request failed (${departmentsResult.reason?.response?.status || 'Network Error'})`;
        errors.push(`Departments: ${formatErrorMessage(errMsg)}`);
      } else {
        errors.push(`Departments: ${formatErrorMessage(departmentsResult.value.data.message)}`);
      }

      // Process clinics data
      let clinics = [];
      if (clinicsResult.status === 'fulfilled' && clinicsResult.value.data.success) {
        clinics = clinicsResult.value.data.clinics || [];
      } else if (clinicsResult.status === 'rejected') {
        const errMsg = clinicsResult.reason?.response?.data?.message || clinicsResult.reason?.message || `Request failed (${clinicsResult.reason?.response?.status || 'Network Error'})`;
        errors.push(`Clinics: ${formatErrorMessage(errMsg)}`);
      } else {
        errors.push(`Clinics: ${formatErrorMessage(clinicsResult.value.data.message)}`);
      }

      // Show combined error message if any errors occurred
      if (errors.length > 0) {
        setAlertState({
          show: true,
          type: 'error',
          title: 'Failed to Load Some Data',
          message: errors.join(' • ')
        });
      }

      // Set the related data
      setRelatedData({
        cases,
        users,
        providers,
        patients,
        departments,
        clinics
      });

    } catch (err) {
      console.error('Error fetching related data:', err);
      setAlertState({
        show: true, type: 'error', title: '',
        message: err.response?.data?.message || err.message || 'Failed to load health system data. Try again later.'
      });
    } finally {
      setLoadingRelated(false);
    }
  };

  // Function to handle selecting a health system
  const handleSelectSystem = async (system) => {
    try {
      setLoadingRelated(true);
      setShowDetailsSidebar(true);
      setActiveTab('cases');
      // Fetch full health system details
      const detailsResponse = await adminApi.get(`/admin/health-system/${system.health_system_id}/`);
      if (detailsResponse.data.success) {
        setSelectedSystem(detailsResponse.data.health_system);
      } else {
        setSelectedSystem(system); // Fallback to list data
      }
      // Fetch related data
      fetchRelatedData(system);

      // Navigate to URL with health system ID
      navigate(`/admin/health-systems/${system.id}`);
    } catch (err) {
      setSelectedSystem(system); // Fallback to list data
      fetchRelatedData(system);
      navigate(`/admin/health-systems/${system.id}`);
    }
  };

  // Function to handle closing the details sidebar
  const handleCloseSidebar = () => {
    setShowDetailsSidebar(false);
    setSelectedSystem(null);
    // Navigate back to base URL to remove the ID from URL
    navigate('/admin/health-systems');
  };

  return (
    <>
      {/* Page Title */}
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0 font-size-18">Health Systems Management</h4>
            <div className="page-title-right">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item"><a href="/admin">Admin</a></li>
                  <li className="breadcrumb-item active">Health Systems</li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </div>
      
      {/* Action buttons */}
      <div className="d-flex justify-content-end align-items-center mb-4">
        <Link to="/admin/health-systems/create" className="btn btn-primary disabled">
          <i className="fa fa-plus me-1"></i> Create New
        </Link>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {isLoading ? (
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : healthSystems.length === 0 ? (
        <div className="alert text-secondary text-center">
          <p>No health systems found.</p>
        </div>
      ) : (
        <AgGridTable
          rowData={healthSystems}
          columnDefs={columnDefs}
          defaultColDef={{
            flex: 1,
            minWidth: 100,
            resizable: true,
            sortable: true,
            filter: true,
          }}
          gridOptions={{
            autoSizeStrategy: {
              type: 'fitGridWidth'
            }
          }}
          getRowId={(params) => params.data.id}
        />
      )}

      {/* Sidebar for Health System Details */}
      <SidebarPanel
        isOpen={showDetailsSidebar && selectedSystem !== null}
        onClose={handleCloseSidebar}
        title="Health System Details"
        className="health-system-sidebar"
      >
        {alertState.show && (
          <AlertMessage
            type={alertState.type}
            title={alertState.title}
            message={alertState.message}
            onDismiss={() => setAlertState({ ...alertState, show: false })}
          />
        )}
        {selectedSystem && (
          <div className="row">
            <div className="col-12">
              <div className="card overflow-hidden">
                <div className="card-body">
                  <div className="d-flex card-title justify-content-between align-items-center mb-3">
                    <h5 className="text-primary mb-0">{selectedSystem.name}</h5>
                    <Link to={`/admin/health-systems/edit/${selectedSystem.health_system_id}`} className="btn btn-primary disabled btn-sm">
                      <i className="fa fa-pencil me-1"></i> Edit
                    </Link>
                  </div>
                  <div className="row">
                    <div className="col-sm-12">
                      <div className="pt-4">
                        <div className="row">
                          <div className="col-6">
                            <h5 className="font-size-15">ID:</h5>
                            <p className="text-muted mb-0">{selectedSystem.id || 'N/A'}</p>
                          </div>
                          <div className="col-6">
                            <h5 className="font-size-15">Address:</h5>
                            <p className="text-muted mb-0">
                              {selectedSystem.street_address || selectedSystem.city || selectedSystem.state ? (
                                <>
                                  {selectedSystem.street_address && <>{selectedSystem.street_address}<br /></>}
                                  {(selectedSystem.city || selectedSystem.state || selectedSystem.zip_code) && (
                                    <>{selectedSystem.city}{selectedSystem.city && selectedSystem.state ? ', ' : ''}{selectedSystem.state} {selectedSystem.zip_code}</>
                                  )}
                                </>
                              ) : 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div className="row mt-4">
                          <div className="col-6">
                            <h5 className="font-size-15">Email:</h5>
                            <p className="text-muted mb-0">{selectedSystem.email || 'N/A'}</p>
                          </div>
                          <div className="col-6">
                            <h5 className="font-size-15">Phone:</h5>
                            <p className="text-muted mb-0">{selectedSystem.phone || 'N/A'}</p>
                          </div>
                        </div>
                        <div className="row mt-4">
                          <div className="col-6">
                            <h5 className="font-size-15">EHR System:</h5>
                            <p className="text-muted mb-0">{selectedSystem.ehr_system || 'N/A'}</p>
                          </div>
                          <div className="col-6">
                            <h5 className="font-size-15">EHR Integration Type:</h5>
                            <p className="text-muted mb-0">{selectedSystem.ehr_integration_type || 'N/A'}</p>
                          </div>
                        </div>
                        {selectedSystem.healthcare_logo_url && (
                          <div className="mt-4">
                            <h5 className="font-size-15">Logo:</h5>
                            <img src={selectedSystem.healthcare_logo_url} alt="Healthcare Logo" className="img-fluid" style={{ maxWidth: '150px' }} />
                          </div>
                        )}
                          <div className="row">
                            <h5 className="font-size-15">Status:</h5>
                            <p className="mb-0">
                              <span className={`badge ${selectedSystem.is_active !== false ? 'bg-success' : 'bg-danger'}`}>
                                {selectedSystem.is_active !== false ? 'Active' : 'Inactive'}
                              </span>
                            </p>
                          </div>
                        <div className="row mt-4">
                          <div className="col-6">
                            <h5 className="font-size-15">Created At:</h5>
                            <p className="text-muted mb-0">{new Date(selectedSystem.created_at).toLocaleString()}</p>
                          </div>
                          <div className="col-6">
                            <h5 className="font-size-15">Last Updated:</h5>
                            <p className="text-muted mb-0">{new Date(selectedSystem.updated_at).toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <h4 className="card-title text-primary mb-4">Statistics</h4>
                  {loadingRelated ? (
                    <div className="d-flex justify-content-center p-3">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading statistics...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="row">
                      <div className="col-4 mb-3">
                        <div className="d-flex">
                          <div className="avatar-xs me-3">
                            <span className="avatar-title rounded-circle bg-primary bg-soft text-primary font-size-18">
                              <i className="fa fa-account-multiple"></i>
                            </span>
                          </div>
                          <div>
                            <h5 className="font-size-14 mb-0">{relatedData.patients.length}</h5>
                            <p className="text-muted mb-0">Patients</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-4 mb-3">
                        <div className="d-flex">
                          <div className="avatar-xs me-3">
                            <span className="avatar-title rounded-circle bg-success bg-soft text-success font-size-18">
                              <i className="fa fa-doctor"></i>
                            </span>
                          </div>
                          <div>
                            <h5 className="font-size-14 mb-0">{relatedData.providers.length}</h5>
                            <p className="text-muted mb-0">Providers</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-4 mb-3">
                        <div className="d-flex">
                          <div className="avatar-xs me-3">
                            <span className="avatar-title rounded-circle bg-info bg-soft text-info font-size-18">
                              <i className="fa fa-file-document"></i>
                            </span>
                          </div>
                          <div>
                            <h5 className="font-size-14 mb-0">{relatedData.cases.length}</h5>
                            <p className="text-muted mb-0">Cases</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="d-flex">
                          <div className="avatar-xs me-3">
                            <span className="avatar-title rounded-circle bg-warning bg-soft text-warning font-size-18">
                              <i className="fa fa-account-circle"></i>
                            </span>
                          </div>
                          <div>
                            <h5 className="font-size-14 mb-0">{relatedData.users.length}</h5>
                            <p className="text-muted mb-0">Users</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="d-flex">
                          <div className="avatar-xs me-3">
                            <span className="avatar-title rounded-circle bg-secondary bg-soft text-secondary font-size-18">
                            </span>
                          </div>
                          <div>
                            <h5 className="font-size-14 mb-0">{relatedData.departments.length}</h5>
                            <p className="text-muted mb-0">Departments</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="d-flex">
                          <div className="avatar-xs me-3">
                            <span className="avatar-title rounded-circle bg-dark bg-soft text-dark font-size-18">
                            </span>
                          </div>
                          <div>
                            <h5 className="font-size-14 mb-0">{relatedData.clinics.length}</h5>
                            <p className="text-muted mb-0">Clinics</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <ul className="nav nav-tabs nav-tabs-custom nav-justified" role="tablist">
                    <li className="nav-item">
                      <a 
                        className={`nav-link ${activeTab === 'cases' ? 'active' : ''}`}
                        onClick={() => setActiveTab('cases')}
                        role="tab"
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="d-block d-sm-none"><i className="fa fa-file-document"></i></span>
                        <span className="d-none d-sm-block">Cases</span>
                      </a>
                    </li>
                    <li className="nav-item">
                      <a 
                        className={`nav-link ${activeTab === 'providers' ? 'active' : ''}`}
                        onClick={() => setActiveTab('providers')}
                        role="tab"
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="d-block d-sm-none"><i className="fa fa-doctor"></i></span>
                        <span className="d-none d-sm-block">Providers</span>
                      </a>
                    </li>
                    <li className="nav-item">
                      <a 
                        className={`nav-link ${activeTab === 'patients' ? 'active' : ''}`}
                        onClick={() => setActiveTab('patients')}
                        role="tab"
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="d-block d-sm-none"><i className="fa fa-account-multiple"></i></span>
                        <span className="d-none d-sm-block">Patients</span>
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => setActiveTab('users')}
                        role="tab"
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="d-block d-sm-none"><i className="fa fa-account-circle"></i></span>
                        <span className="d-none d-sm-block">Users</span>
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === 'departments' ? 'active' : ''}`}
                        onClick={() => setActiveTab('departments')}
                        role="tab"
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="d-block d-sm-none"><i className="fa fa-building"></i></span>
                        <span className="d-none d-sm-block">Departments</span>
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === 'clinics' ? 'active' : ''}`}
                        onClick={() => setActiveTab('clinics')}
                        role="tab"
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="d-block d-sm-none"><i className="fa fa-hospital"></i></span>
                        <span className="d-none d-sm-block">Clinics</span>
                      </a>
                    </li>
                  </ul>

                  <div className="tab-content p-3 text-muted">
                    {/* Cases Tab */}
                    <div className={`tab-pane ${activeTab === 'cases' ? 'active' : ''}`} role="tabpanel">
                      <div className="table-responsive">
                        <table className="table table-centered table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Case ID</th>
                              <th>Patient</th>
                              <th>Provider</th>
                              <th>Date</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {relatedData.cases.map(caseItem => (
                              <tr key={caseItem.id}>
                                <td>{caseItem.id}</td>
                                <td>
                                  {caseItem.patient_info_fk ?
                                    `${caseItem.patient_info_fk.first_name} ${caseItem.patient_info_fk.last_name}` :
                                    'N/A'}
                                </td>
                                <td>
                                  {caseItem.provider_info_fk ?
                                    `${caseItem.provider_info_fk.first_name} ${caseItem.provider_info_fk.last_name}` :
                                    'N/A'}
                                </td>
                                <td>{new Date(caseItem.created_at).toLocaleDateString()}</td>
                                <td>
                                  <span className={`badge ${
                                    caseItem.mira_sol_case_status === 'completed' ? 'bg-success' :
                                    caseItem.mira_sol_case_status === 'token_generated' ? 'bg-info' : 'bg-warning'
                                  }`}>
                                    {caseItem.mira_sol_case_status}
                                  </span>
                                </td>
                                <td>
                                  <Link to={`/admin/cases/${caseItem.id}/audit-logs`} className="btn btn-sm btn-primary">
                                    View
                                  </Link>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    
                    {/* Providers Tab */}
                    <div className={`tab-pane ${activeTab === 'providers' ? 'active' : ''}`} role="tabpanel">
                      <div className="table-responsive">
                        <table className="table table-centered table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Provider ID</th>
                              <th>Name</th>
                              <th>Specialty</th>
                              <th>Patients</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {relatedData.providers.map(provider => (
                              <tr key={provider.id}>
                                <td>{provider.provider_id}</td>
                                <td>{`${provider.first_name} ${provider.last_name}`}</td>
                                <td>{provider.specialty || 'N/A'}</td>
                                <td>-</td>
                                <td>
                                  <span className="badge bg-success">
                                    Active
                                  </span>
                                </td>
                                <td>
                                  <Link to={`/admin/providers/${provider.provider_id}`} className="btn btn-sm btn-primary">
                                    View
                                  </Link>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    
                    {/* Patients Tab */}
                    <div className={`tab-pane ${activeTab === 'patients' ? 'active' : ''}`} role="tabpanel">
                      <div className="table-responsive">
                        <table className="table table-centered table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Core server ID</th>
                              <th>Name (DOB)</th>
                              <th>Primary phone</th>
                              <th>Primary email</th>
                              <th>MiraSol patient ID</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {relatedData.patients.map(patient => (
                              <tr key={patient.id}>
                                <td>{patient.patient_id}</td>
                                <td>{`${patient.first_name} ${patient.last_name} (${patient.dob})`}</td>
                                <td>{patient.phone}</td>
                                <td>{patient.email}</td>
                                <td>{patient.id}</td>
                                <td>
                                  <span className="badge bg-success">
                                    Active
                                  </span>
                                </td>
                                <td>
                                  <Link to={`/admin/patients/${patient.patient_id}`} className="btn btn-sm btn-primary">
                                    View
                                  </Link>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    
                    {/* Users Tab */}
                    <div className={`tab-pane ${activeTab === 'users' ? 'active' : ''}`} role="tabpanel">
                      <div className="table-responsive">
                        <table className="table table-centered table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>User ID</th>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Role</th>
                              <th>Last Login</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {relatedData.users && relatedData.users.length > 0 ? (
                              relatedData.users.map(user => (
                                <tr key={user.id}>
                                  <td>{user.id}</td>
                                  <td>{user.name}</td>
                                  <td>{user.email}</td>
                                  <td>{user.role}</td>
                                  <td>{user.last_login}</td>
                                  <td>
                                    <Link to={`/admin/users/${user.id}`} className="btn btn-sm btn-primary">
                                      View
                                    </Link>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="text-center">No users found for this health system</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Departments Tab */}
                    <div className={`tab-pane ${activeTab === 'departments' ? 'active' : ''}`} role="tabpanel">
                      <div className="table-responsive">
                        <table className="table table-centered table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Department ID</th>
                              <th>Name</th>
                              <th>Created At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {relatedData.departments && relatedData.departments.length > 0 ? (
                              relatedData.departments.map(dept => (
                                <tr key={dept.id}>
                                  <td>{dept.id}</td>
                                  <td>{dept.name}</td>
                                  <td>{dept.created_at ? new Date(dept.created_at).toLocaleDateString() : 'N/A'}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="3" className="text-center">No departments found for this health system</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Clinics Tab */}
                    <div className={`tab-pane ${activeTab === 'clinics' ? 'active' : ''}`} role="tabpanel">
                      <div className="table-responsive">
                        <table className="table table-centered table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Clinic ID</th>
                              <th>Name</th>
                              <th>Created At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {relatedData.clinics && relatedData.clinics.length > 0 ? (
                              relatedData.clinics.map(clinic => (
                                <tr key={clinic.id}>
                                  <td>{clinic.id}</td>
                                  <td>{clinic.name}</td>
                                  <td>{clinic.created_at ? new Date(clinic.created_at).toLocaleDateString() : 'N/A'}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="3" className="text-center">No clinics found for this health system</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </SidebarPanel>
    </>
  );
};

export default HealthSystemsPage;